import { Box, Button, Card, Chip, List, ListItem, ListItemText, Stack, Typography } from '@mui/material';
import { DoneAll, Notifications as NotifIcon } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../shared/api';
import type { Notification } from '../../../shared/types';
import PageHeader from '../../../shared/PageHeader';
import { formatDateTime } from '../../../shared/utils';
import { useNavigate } from 'react-router-dom';

export default function NotificationsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => (await api.get<Notification[]>('/notifications')).data,
  });

  const markRead = useMutation({
    mutationFn: async (id: string) => (await api.patch(`/notifications/${id}/read`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const markAll = useMutation({
    mutationFn: async () => (await api.patch('/notifications/read-all')).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const unread = (data ?? []).filter((n) => !n.readAt).length;

  return (
    <Box>
      <PageHeader
        title="Notifications"
        subtitle={`${unread} unread`}
        action={unread > 0 && <Button startIcon={<DoneAll />} onClick={() => markAll.mutate()}>Mark all read</Button>}
      />

      {(data?.length ?? 0) === 0 ? (
        <Card sx={{ p: 6, textAlign: 'center' }}>
          <NotifIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
          <Typography sx={{ mt: 2 }} color="text.secondary">No notifications.</Typography>
        </Card>
      ) : (
        <Card>
          <List disablePadding>
            {(data ?? []).map((n) => (
              <ListItem
                key={n.id} divider button={true as any}
                sx={{ bgcolor: !n.readAt ? 'rgba(245,158,11,0.08)' : undefined, py: 1.5 }}
                onClick={() => {
                  if (!n.readAt) markRead.mutate(n.id);
                  if (n.link) navigate(n.link);
                }}
              >
                <ListItemText
                  primary={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {!n.readAt && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'secondary.main' }} />}
                      <Typography sx={{ fontWeight: !n.readAt ? 700 : 400 }}>{n.title}</Typography>
                      <Chip size="small" label={n.type} variant="outlined" />
                    </Stack>
                  }
                  secondary={
                    <>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>{n.body}</Typography>
                      <Typography variant="caption" color="text.secondary">{formatDateTime(n.createdAt)}</Typography>
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Card>
      )}
    </Box>
  );
}
